import { IAlanI, EsitlikTuru } from '../../../interfaces/IAlan';
import { IParametre, ITurParametre } from '../../../interfaces/IParametre/IParametre';

export interface IDinamikBilesenHesapAlani {
  /** Benzersiz id (ör: '_hesap.olculer.tek'). Kullanıcıya göstermek için baslik kullanılır. */
  ismi: string;
  /** MongoDB üst seviye alan yolu (ör: '_hesap.olculer'). Yoksa ismi kullanılır. */
  alanYolu?: string;
  baslik: string;
  simge?: string;
  /** Filtre tipi: yazı/sayı/tarih/secim/olcu-uclu (3 sayısal ölçü sırasız eşleşme) / olcu-sinir (3 sıralı ölçüye min-max sınır) */
  tipi: 'yazı' | 'sayı' | 'tarih' | 'secim' | 'olcu-uclu' | 'olcu-sinir';
  /** Açılır kutu seçenekleri (tipi='secim' için) */
  secenekler?: { etiket: string; deger: any }[];
  /** Çoklu seçim yapılabilsin mi */
  coklu?: boolean;
}

export interface IDinamikBilesenFiltreSet {
  id: string;
  setId: string;
  baslik: string;
  simge?: string;
  parametreler: IParametre[];
  /** Bileşene özgü hesap alanları (prepipe ile hesaplanan, parametre değil üst seviye) */
  hesapAlanlari?: IDinamikBilesenHesapAlani[];
}

/** Filtrelenemeyen (gorsel/yapisal) parametre turleri */
const GORSEL_TIPLER: ITurParametre[] = [
  'Simgeli Başlık', 'Grup', 'Sekme', '2liSatır', '3lüSatır',
  'İmza', 'Çizim', 'Resim', 'Video', 'Ses', 'Dosya',
  'Dosya Yükleme', 'Sürükle Bırak', 'Aranjman'
];

/** Bir parametrenin filtrelenebilir olup olmadığını kontrol eder */
export function filtrelenebilirMi(p: IParametre): boolean {
  return !GORSEL_TIPLER.includes(p.turu);
}

/** Tek bir IParametre'yi IAlanI'ya donusturur */
export function parametredenAlanOlustur(p: IParametre): IAlanI {
  const alan: IAlanI = {
    ismi: p.ismi,
    baslik: p.baslik ?? p.ismi,
    alanIsmi: 'parametreler',
    parametreFiltresi: true,
    parametreIsmi: p.ismi,
    parametreTuru: p.turu,
    yerTutucu: p.yerTutucu ?? p.baslik ?? p.ismi,
    ipucu: p.ipucu,
    aciklama: p.aciklama,
    solSimge: p.ayarlar?.solSimge,
  };

  switch (p.turu) {
    case 'Yazı':
    case 'Yazı Alanı':
    case 'Parola':
    case 'RichText':
    case 'Kod':
    case 'KareKod':
    case 'BarKod':
    case 'Matematik':
    case 'Renk':
      alan.tipi = 'Yazı Kutusu';
      alan.esitlikTurleri = ['=', '!=', 'içerir', 'içermez', 'Boş Olan', 'Boş Olmayan'];
      alan.aktifEsitlikTuru = 'içerir';
      break;

    case 'Sayı':
    case 'Para':
    case 'İlerleme Çubuğu':
      alan.tipi = 'Sayı Kutusu';
      alan.esitlikTurleri = ['=', '!=', '<', '<=', '>', '>=', 'aralık', 'Boş Olan', 'Boş Olmayan'];
      alan.aktifEsitlikTuru = '=';
      break;

    case 'Tarih':
    case 'Tarih Saat':
    case 'Ay':
    case 'Hafta':
    case 'Saat':
      alan.tipi = 'Tarih Kutusu';
      alan.esitlikTurleri = ['=', '!=', '<', '<=', '>', '>=', 'Boş Olan', 'Boş Olmayan'];
      alan.aktifEsitlikTuru = '=';
      break;

    case 'Doğruluk':
    case 'Onay':
    case 'Anahtar':
      alan.tipi = 'Kayar Seçim Kutusu';
      alan.ek = {
        liste: [
          { label: 'Hepsi', value: null },
          { label: 'Evet', value: true },
          { label: 'Hayır', value: false },
        ]
      };
      alan.degeri = 0;
      break;

    case 'Açılır Kutu':
    case 'Liste':
      alan.tipi = 'Özel Açılır Kutu';
      if (p.ayarlar?.secenekler) {
        alan.ek = {
          liste: p.ayarlar.secenekler.map((s: any) =>
            typeof s === 'string'
              ? { baslik: s, degeri: s }
              : { baslik: s.etiket, degeri: s.deger }
          )
        };
      }
      break;

    case 'Konum':
      alan.tipi = 'Yazı Kutusu';
      alan.esitlikTurleri = ['Boş Olan', 'Boş Olmayan'];
      alan.aktifEsitlikTuru = 'Boş Olmayan';
      break;

    case 'XYZ':
      alan.tipi = 'Yazı Kutusu';
      alan.esitlikTurleri = ['Boş Olan', 'Boş Olmayan'];
      alan.aktifEsitlikTuru = 'Boş Olmayan';
      break;

    case 'Oylama':
      alan.tipi = 'Özel Açılır Kutu';
      if (p.ayarlar?.secenekler) {
        alan.ek = {
          liste: (p.ayarlar as any).secenekler.map((s: any) => ({
            baslik: s.etiket,
            degeri: s.deger
          }))
        };
      }
      break;

    case 'Zaman Aralığı':
      alan.tipi = 'Tarih Kutusu';
      alan.esitlikTurleri = ['<', '<=', '>', '>=', 'Boş Olan', 'Boş Olmayan'];
      alan.aktifEsitlikTuru = '>=';
      break;

    case 'Tablo':
      alan.tipi = 'Yazı Kutusu';
      alan.esitlikTurleri = ['Boş Olan', 'Boş Olmayan'];
      alan.aktifEsitlikTuru = 'Boş Olmayan';
      break;

    default:
      alan.tipi = 'Yazı Kutusu';
      alan.esitlikTurleri = ['=', '!=', 'içerir', 'içermez'];
      alan.aktifEsitlikTuru = 'içerir';
  }

  return alan;
}

/** IParametre[] dizisinden siralama secenekleri olusturur */
export function parametredenSiralamaOlustur(parametreler: IParametre[]): { ismi: string; degeri: any }[] {
  return [];
  if (!parametreler?.length) return [];

  const EMOJI_MAP: Record<string, string> = {
    'Yazı': '📝', 'Yazı Alanı': '📝', 'Sayı': '🔢', 'Para': '💰',
    'Tarih': '📅', 'Tarih Saat': '📅', 'Saat': '🕐',
    'Açılır Kutu': '📋', 'Doğruluk': '✅', 'Onay': '✅',
  };

  const sonuc: { ismi: string; degeri: any }[] = [];
  const eklenen = new Set<string>();

  for (const p of parametreler) {
    if (GORSEL_TIPLER.includes(p.turu)) continue;
    if (['Dosya Kutusu', 'Dinamik Bileşen'].includes(p.turu)) continue;
    if (!p.ismi || eklenen.has(p.ismi)) continue;
    eklenen.add(p.ismi);

    const emoji = EMOJI_MAP[p.turu] || '📌';
    const baslik = p.baslik || p.ismi;
    const alan = 'veri.icerikler.ek.' + p.ismi;

    if (['Sayı', 'Para', 'İlerleme Çubuğu'].includes(p.turu)) {
      sonuc.push({ ismi: `${emoji} ${baslik} ↓`, degeri: { [alan]: -1 } });
      sonuc.push({ ismi: `${emoji} ${baslik} ↑`, degeri: { [alan]: 1 } });
    } else if (['Tarih', 'Tarih Saat', 'Saat', 'Ay', 'Hafta'].includes(p.turu)) {
      sonuc.push({ ismi: `${emoji} ${baslik} (Yeni→Eski)`, degeri: { [alan]: -1 } });
      sonuc.push({ ismi: `${emoji} ${baslik} (Eski→Yeni)`, degeri: { [alan]: 1 } });
    } else {
      sonuc.push({ ismi: `${emoji} ${baslik} A→Z`, degeri: { [alan]: 1 } });
      sonuc.push({ ismi: `${emoji} ${baslik} Z→A`, degeri: { [alan]: -1 } });
    }
  }

  return sonuc;
}

/** IParametre[] dizisini IAlanI[] dizisine donusturur (filtrelenebilir olanlar) */
export function parametredenFilitreAlanlariOlustur(parametreler: IParametre[]): IAlanI[] {
  if (!parametreler?.length) return [];
  return parametreler
    .filter(p => filtrelenebilirMi(p))
    .map(p => parametredenAlanOlustur(p));
}

/** Bilesen secim alani olusturur (filtreci icerisinde chip-bar yerine alan sistemi olarak) */
export function bilesenSecimAlaniOlustur(tipler: {id: string; baslik: string; renk?: string; simge?: string}[]): IAlanI {
  return {
    ismi: '_bilesenTipi',
    baslik: 'Kayıt Türü',
    tipi: 'Bileşen Seçimi',
    bilesenSecenekleri: tipler,
    degeri: [],
    esitlikTurleri: ['içerir', 'içermez'] as EsitlikTuru[],
    aktifEsitlikTuru: 'içerir' as EsitlikTuru,
  };
}

/** Serbest alan filtresi olusturur (wildcard alan yolu girebilme) */
export function serbestAlanOlustur(): IAlanI {
  return {
    ismi: '_serbestAlan_' + Date.now(),
    baslik: 'Serbest Alan',
    tipi: 'Serbest Alan',
    solSimge: '🔗',
    serbestAlanIsmi: '',
    serbestAlanTipiIdx: 0,
    serbestAlanTipi: 'yazı',
    esitlikTurleri: ['=', '!=', 'içerir', 'içermez', 'Boş Olan', 'Boş Olmayan'] as EsitlikTuru[],
    aktifEsitlikTuru: 'içerir' as EsitlikTuru,
    degeri: null,
  };
}

/** Dinamik bilesen filtresi olusturur (bilesen sec → alan sec → deger gir) */
export function dinamikBilesenFiltreAlaniOlustur(): IAlanI {
  return {
    ismi: '_dbFiltre_' + Date.now(),
    baslik: 'Dinamik Bileşen Filtresi',
    tipi: 'Dinamik Bileşen Filtresi',
    solSimge: '🧩',
    dinamikBilesenId: null,
    dinamikBilesenAlanIsmi: null,
    dinamikBilesenSetId: null,
    serbestAlanTipi: 'yazı',
    esitlikTurleri: ['=', '!=', 'içerir', 'içermez', 'Boş Olan', 'Boş Olmayan'] as EsitlikTuru[],
    aktifEsitlikTuru: 'içerir' as EsitlikTuru,
    degeri: null,
  };
}

/** Kart temel alanlari icin IAlanI listesi olusturur */
export function kartTemelFilitreAlanlari(): IAlanI[] {
  return [
    {
      ismi: 'sahip',
      baslik: 'Sahip',
      alanIsmi: 'sahip',
      tipi: 'Üye Seçimi',
      solSimge: '👤',
      yerTutucu: 'Üye ara...',
      esitlikTurleri: ['=', '!=', 'Olan', 'Olmayan'],
      aktifEsitlikTuru: '=',
      degeri: null,
    },
    {
      ismi: 'tarih',
      baslik: 'Tarih',
      alanIsmi: 'tarih',
      tipi: 'Tarih Kutusu',
      solSimge: '📅',
      yerTutucu: 'Tarih',
      esitlikTurleri: ['=', '!=', '<', '<=', '>', '>=', 'Olan', 'Olmayan'],
      aktifEsitlikTuru: '>=',
    },
    {
      ismi: 'gizlilik',
      baslik: 'Gizlilik',
      alanIsmi: 'gizlilik',
      tipi: 'Özel Açılır Kutu',
      solSimge: '🔒',
      yerTutucu: 'Gizlilik',
      esitlikTurleri: ['=', '!=', 'Olan', 'Olmayan'],
      aktifEsitlikTuru: '=',
      ek: {
        liste: [
          { baslik: 'Herkese Açık', degeri: 'Herkese Açık' },
          { baslik: 'Sadece Ben', degeri: 'Sadece Ben' },
          { baslik: 'Takipleştiklerinizle', degeri: 'Takipleştiklerinizle' },
          { baslik: 'Listedekilerle', degeri: 'Listedekilerle' },
          { baslik: 'Sistem', degeri: 'Sistem' },
        ]
      },
    },
    {
      ismi: 'etiket_arama',
      baslik: 'Etiket',
      alanIsmi: 'etiketler.degeri',
      tipi: 'Yazı Kutusu',
      solSimge: '🏷️',
      yerTutucu: 'Etiket değeri',
      esitlikTurleri: ['=', '!=', 'içerir', 'içermez', 'Olan', 'Olmayan'],
      aktifEsitlikTuru: 'içerir',
    },
    {
      ismi: '_altKartSayisi',
      baslik: 'Alt Kart Sayısı',
      alanIsmi: 'yorumSayisi',
      tipi: 'Sayı Kutusu',
      solSimge: '📎',
      yerTutucu: 'Sayı',
      esitlikTurleri: ['=', '!=', '<', '<=', '>', '>=', 'Olan', 'Olmayan'],
      aktifEsitlikTuru: '>',
      cevirMongoya: (alan: any) => {
        const deger = Number(alan.degeri) || 0;
        const alanAdi = alan.alanIsmi || 'yorumSayisi';
        const degeriVar = alan.aktifEsitlikTuru === 'Olan' || alan.aktifEsitlikTuru === 'Boş Olmayan';
        const degeriYok = alan.aktifEsitlikTuru === 'Olmayan' || alan.aktifEsitlikTuru === 'Boş Olan';
        if (degeriVar) return { [alanAdi]: { $gt: 0 } };
        if (degeriYok) return { $or: [{ [alanAdi]: { $exists: false } }, { [alanAdi]: null }, { [alanAdi]: 0 }] };
        const ops: Record<string, any> = { '=': '$eq', '!=': '$ne', '>': '$gt', '>=': '$gte', '<': '$lt', '<=': '$lte' };
        const op = ops[alan.aktifEsitlikTuru] || '$gt';
        return { $or: [{ [alanAdi]: { [op]: deger } }, ...(deger <= 0 ? [{ [alanAdi]: { $exists: false } }, { [alanAdi]: null }] : [])] };
      },
    },
  ];
}
